import { Component, h, Prop, State, Element, Event, EventEmitter, Listen, Method } from '@stencil/core';
import { getClassListString } from '../component-utils/stencilUtils';

@Component({
  tag: 'aafp-modal',
  shadow: false,
})
export class AafpModal {
  /** For setting the unique identifier of the modal  */
  @Prop() actionId!: string;
  /** For setting the heading of the modal  */
  @Prop() heading: string;
  /** For setting the size of the modal  */
  @Prop({ mutable: true }) modalType: 'basic' | 'large' = 'basic';
  /** Prop for determining if the close button (X) is displayed  */
  @Prop() showClose: boolean = false;
  /**
   * Configures correct behavior / display depending on the wcmmode in AEM. In edit or design mode the modal content will display inline.
   */
  @Prop({ mutable: true }) wcmmode: 'edit' | 'design' | 'preview' | 'analytics' | 'read_only' | 'disabled' = 'disabled';

  @State() hidden: boolean = true;

  @Element() element: HTMLElement;

  @Event() modalkeyup: KeyboardEvent;

  /**
   * When this modal is closed, emit an event with the ID
   * @param modalId
   */
  @Event() modalHidden: EventEmitter<string>;
  modalHiddenHandler(modalId: string) {
    this.modalHidden.emit(modalId);
  }

  /**
   * When this modal is opened, emit an event with the ID
   * @param modalId
   */
  @Event() modalShown: EventEmitter<string>;
  modalShownHandler(modalId: string) {
    this.modalShown.emit(modalId);
  }

  private dialogElement: HTMLElement;
  private firstFocusableEl: HTMLElement;
  private lastFocusableEl: HTMLElement;

  //For logging element that opened modal
  private modalInitEl: HTMLElement;

  /**
   * Listens for keyup event on @param el.
   * If key is Escape, modal is "closed".
   * If key is Tab or Shift+Tab, elements are tabbed through
   */
  @Listen('keydown', { target: 'document' })
  keypressEventHandler(ev: KeyboardEvent) {
    if (!this.hidden) {
      if (ev.key === 'Escape') {
        this.hideModal(this.actionId);
      } else if (ev.shiftKey && ev.keyCode === 9) {
        if (document.activeElement === this.firstFocusableEl) {
          this.lastFocusableEl.focus();
          ev.preventDefault();
        }
      } else if (ev.keyCode === 9) {
        if (document.activeElement === this.lastFocusableEl) {
          this.firstFocusableEl.focus();
          ev.preventDefault();
        }
      }
    }
  }

  /**
   * Exposed function for showing modal from HTML page.
   */
  @Method()
  async showModal(id: string) {
    this.hidden = false;
    this.modalShownHandler(id);
    this.setPageAriaHidden(true);
    this.modalInitEl = document.activeElement as HTMLElement;
  }

  @Method()
  async hideModal(id: string) {
    this.hidden = true;
    this.modalHiddenHandler(id);
    this.setPageAriaHidden(false);
    this.modalInitEl.focus();
  }

  private setPageAriaHidden(hide: boolean) {
    const children = document.querySelectorAll('body > *:not(#' + this.actionId + ')');
    children.forEach((element) => {
      if (hide) element.setAttribute('aria-hidden', 'true');
      else element.removeAttribute('aria-hidden');
    });
  }

  private resetVerticalCenter() {
    const offset = (window.innerHeight - this.dialogElement.offsetHeight) / 2;
    this.dialogElement.style.top = `${window.pageYOffset + Math.max(offset, 0)}px`;
  }

  componentDidLoad() {
    if (this.wcmmode === 'edit' || this.wcmmode === 'design') {
      return;
    }

    document.body.appendChild(this.dialogElement);

    var focusableEls = document
      .getElementById(this.actionId)
      ?.querySelectorAll(
        '[tabindex], a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled])',
      );
    if (focusableEls !== undefined && focusableEls.length > 0) {
      this.firstFocusableEl = focusableEls[0] as HTMLElement;
      this.lastFocusableEl = focusableEls[focusableEls.length - 1] as HTMLElement;
    }
  }

  disconnectedCallback() {
    this.dialogElement.parentNode?.removeChild(this.dialogElement);
  }

  componentDidUpdate() {
    if (!this.hidden) {
      this.resetVerticalCenter();
      this.firstFocusableEl.focus();
    }
  }

  render() {
    return (
      <div
        role="dialog"
        aria-modal="true"
        id={this.actionId}
        class={getClassListString(
          {
            [`adl-modal--author-mode-${this.wcmmode}`]: this.wcmmode !== 'disabled',
            'adl-modal__hide': this.hidden,
          },
          'adl-modal adl-modal-wrapper',
        )}
        ref={(el) => (this.dialogElement = el as HTMLElement)}
      >
        <section
          aria-labelledby={this.heading ? `${this.actionId}-heading` : undefined}
          aria-modal="true"
          class={this.modalType ? `adl-modal__${this.modalType}` : ''}
        >
          <div class="adl-modal__container" id={`${this.actionId}_modal-container`}>
            <div class="adl-modal__header">
              {this.showClose && (
                <button
                  class="close"
                  id={`${this.actionId}_close-button`}
                  aria-labelledby="Close Dialog"
                  onClick={() => this.hideModal(this.actionId)}
                >
                  <i class="material-icons aafp-modal_close">clear</i>
                </button>
              )}
              <div class="adl-modal__header-text">
                <slot name="header"></slot>
              </div>
            </div>
            <div class="adl-modal__content adl-p-around_medium" id={`${this.actionId}_modal-content`}>
              {this.heading && (
                <h2 id={`${this.actionId}-heading`} class="adl-h2">
                  {this.heading}
                </h2>
              )}
              <slot name="content"></slot>
            </div>
            <div class="adl-modal__footer adl-space__sm__p-v-md">
              <slot name="actions"></slot>
            </div>
          </div>
        </section>
        <div class="adl-modal_backdrop" onClick={() => this.hideModal(this.actionId)}></div>
      </div>
    );
  }
}
